import {
  Component,
  Input,
  signal,
  computed,
  ChangeDetectionStrategy,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { TooltipModule } from '@progress/kendo-angular-tooltip';

/**
 * Cache status indicator component for displaying cache metadata at bottom of grids
 * Shows: "Cached as of MM/DD/YYYY HH:MM:SS (567 items)" with freshness indicator
 */
@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'ups-cache-status',
  imports: [CommonModule, TooltipModule],
  template: `
    @if (cacheTimestamp()) {
      <div
        class="cache-status"
        [class.fresh]="isFresh()"
        [class.stale]="isStale()"
      >
        <span class="cache-icon" [title]="statusTooltip()">
          {{ statusIcon() }}
        </span>
        <span class="cache-text">
          Cached as of {{ formattedTimestamp() }}
          @if (itemCount() > 0) {
            <span class="cache-items">({{ itemCount() }} items)</span>
          }
        </span>
      </div>
    }
  `,
  styles: [
    `
      .cache-status {
        display: flex;
        align-items: center;
        gap: 6px;
        padding: 4px 8px;
        font-size: 12px;
        color: #666;
        background-color: #f8f9fa;
        border-top: 1px solid #dee2e6;
        font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
      }

      .cache-icon {
        font-size: 14px;
        cursor: help;
      }

      .cache-status.fresh .cache-icon {
        color: #28a745; /* Green */
      }

      .cache-status.stale .cache-icon {
        color: #dc3545; /* Red */
      }

      .cache-status:not(.fresh):not(.stale) .cache-icon {
        color: #ffc107; /* Yellow */
      }

      .cache-text {
        flex: 1;
      }

      .cache-items {
        color: #999;
        margin-left: 4px;
      }
    `,
  ],
})
export class CacheStatusComponent {
  // Inputs (using signals)
  cacheTimestamp = signal<Date | null>(null);
  itemCount = signal<number>(0);
  cacheSource = signal<string>('');

  // Computed values
  ageInSeconds = computed(() => {
    const timestamp = this.cacheTimestamp();
    if (!timestamp) return null;
    return (Date.now() - timestamp.getTime()) / 1000;
  });

  isFresh = computed(() => {
    const age = this.ageInSeconds();
    return age !== null && age < 60; // < 1 minute = fresh
  });

  isStale = computed(() => {
    const age = this.ageInSeconds();
    return age !== null && age > 180; // > 3 minutes = stale
  });

  statusIcon = computed(() => {
    if (this.isFresh()) return '🟢';
    if (this.isStale()) return '🔴';
    return '🟡';
  });

  statusTooltip = computed(() => {
    const age = this.ageInSeconds();
    if (age === null) return 'Cache status unknown';

    if (this.isFresh()) return `Cache is fresh (${age.toFixed(0)}s old)`;
    if (this.isStale())
      return `Cache is stale (${age.toFixed(0)}s old) - consider refreshing`;
    return `Cache age: ${age.toFixed(0)}s`;
  });

  formattedTimestamp = computed(() => {
    const timestamp = this.cacheTimestamp();
    if (!timestamp) return 'Unknown';

    // Format in user's local timezone: MM/DD/YYYY HH:MM:SS
    const month = String(timestamp.getMonth() + 1).padStart(2, '0');
    const day = String(timestamp.getDate()).padStart(2, '0');
    const year = timestamp.getFullYear();
    const hours = String(timestamp.getHours()).padStart(2, '0');
    const minutes = String(timestamp.getMinutes()).padStart(2, '0');
    const seconds = String(timestamp.getSeconds()).padStart(2, '0');

    return `${month}/${day}/${year} ${hours}:${minutes}:${seconds}`;
  });

  // Legacy @Input support for non-signal components
  @Input() set timestamp(value: Date | null) {
    this.cacheTimestamp.set(value);
  }

  @Input() set count(value: number) {
    this.itemCount.set(value);
  }

  @Input() set source(value: string) {
    this.cacheSource.set(value);
  }
}
